import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Navigation } from 'lucide-react';
import { CompetitorWithDistance } from '../../services/competitorQueryService';

interface CompetitorMapProps {
  subjectProperty: {
    latitude: number;
    longitude: number;
    address: string;
  };
  competitors: CompetitorWithDistance[];
  radiusMiles: number;
}

// Global type declaration for Google Maps
// Note: Also declared in analytics/CompetitorMap.tsx - keep in sync
declare global {
  interface Window {
    google: typeof google;
  }
}

export function CompetitorMap({ subjectProperty, competitors, radiusMiles }: CompetitorMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedCompetitor, setSelectedCompetitor] = useState<CompetitorWithDistance | null>(null);

  // Load Google Maps script
  useEffect(() => {
    if (window.google && window.google.maps) {
      setMapLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => setMapLoaded(true);
    script.onerror = () => console.error('Failed to load Google Maps');
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !window.google) return;

    const map = new google.maps.Map(mapRef.current, {
      zoom: 13,
      center: {
        lat: subjectProperty.latitude,
        lng: subjectProperty.longitude
      },
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
      styles: [
        {
          featureType: 'poi.business',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ]
    });

    mapInstanceRef.current = map;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Add subject property marker (green pin)
    const subjectMarker = new google.maps.Marker({
      position: {
        lat: subjectProperty.latitude,
        lng: subjectProperty.longitude
      },
      map: map,
      title: 'Your Property',
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
            <circle cx="20" cy="20" r="15" fill="#10B981" stroke="#ffffff" stroke-width="4"/>
            <circle cx="20" cy="20" r="8" fill="#ffffff"/>
          </svg>
        `),
        scaledSize: new google.maps.Size(40, 40),
        anchor: new google.maps.Point(20, 20)
      },
      zIndex: 1000
    });

    const subjectInfoWindow = new google.maps.InfoWindow({
      content: `
        <div style="padding: 10px; min-width: 200px;">
          <h3 style="margin: 0 0 8px 0; color: #10B981; font-size: 15px; font-weight: 600;">
            🎯 Your Property
          </h3>
          <p style="margin: 0; font-size: 13px; color: #6B7280;">${subjectProperty.address}</p>
        </div>
      `
    });

    subjectMarker.addListener('click', () => {
      subjectInfoWindow.open(map, subjectMarker);
    });

    markersRef.current.push(subjectMarker);

    // Add competitor markers (red pins with numbers)
    competitors.forEach((competitor, index) => {
      if (!competitor.Latitude || !competitor.Longitude) return;

      const marker = new google.maps.Marker({
        position: {
          lat: competitor.Latitude,
          lng: competitor.Longitude
        },
        map: map,
        title: competitor.Facility_Address,
        label: {
          text: `${index + 1}`,
          color: '#ffffff',
          fontSize: '12px',
          fontWeight: 'bold'
        },
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
              <circle cx="16" cy="16" r="12" fill="#EF4444" stroke="#ffffff" stroke-width="3"/>
            </svg>
          `),
          scaledSize: new google.maps.Size(32, 32),
          anchor: new google.maps.Point(16, 16),
          labelOrigin: new google.maps.Point(16, 16)
        }
      });

      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div style="padding: 10px; min-width: 220px;">
            <h3 style="margin: 0 0 8px 0; color: #374151; font-size: 14px; font-weight: 600;">
              #${index + 1} ${competitor.Fuel_Brand || 'Unbranded'}
            </h3>
            <p style="margin: 0 0 8px 0; font-size: 12px; color: #6B7280;">${competitor.Facility_Address}</p>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 6px; font-size: 11px; color: #374151;">
              <div><strong>Distance:</strong> ${competitor.distance_miles?.toFixed(2)} mi</div>
              <div><strong>Building:</strong> ${competitor.Cstore_Size_Measured?.toLocaleString() || '—'} sf</div>
              <div><strong>Canopy:</strong> ${competitor.Fueling_Canopy_Size_Measured?.toLocaleString() || '—'} sf</div>
              <div><strong>Year:</strong> ${competitor.Year_Built_Public_Record || '—'}</div>
            </div>
          </div>
        `
      });

      marker.addListener('click', () => {
        infoWindow.open(map, marker);
        setSelectedCompetitor(competitor);
      });

      markersRef.current.push(marker);
    });

    // Add radius circle
    new google.maps.Circle({
      strokeColor: '#10B981',
      strokeOpacity: 0.4,
      strokeWeight: 2,
      fillColor: '#10B981',
      fillOpacity: 0.1,
      map: map,
      center: {
        lat: subjectProperty.latitude,
        lng: subjectProperty.longitude
      },
      radius: radiusMiles * 1609.34 // Convert miles to meters
    });

    // Fit bounds to show all markers
    if (competitors.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      bounds.extend({ lat: subjectProperty.latitude, lng: subjectProperty.longitude });
      competitors.forEach(comp => {
        if (comp.Latitude && comp.Longitude) {
          bounds.extend({ lat: comp.Latitude, lng: comp.Longitude });
        }
      });
      map.fitBounds(bounds);
    }
  }, [mapLoaded, subjectProperty, competitors, radiusMiles]);

  if (!mapLoaded) {
    return (
      <div className="w-full h-[500px] bg-gray-100 rounded-lg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-sm text-gray-600">Loading map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Map Legend */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-green-500 border-2 border-white shadow"></div>
            <span className="text-sm text-gray-700">Your Property</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-red-500 border-2 border-white shadow"></div>
            <span className="text-sm text-gray-700">Competitors ({competitors.length})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full border-2 border-green-400 bg-green-50"></div>
            <span className="text-sm text-gray-700">{radiusMiles} mile radius</span>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="relative w-full h-[500px] rounded-lg overflow-hidden border shadow-lg">
        <div ref={mapRef} className="w-full h-full" />
      </div>

      {/* Map Info */}
      <div className="bg-gray-50 border rounded-lg p-4">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-blue-600" />
            <span>
              Showing {competitors.length} competitor{competitors.length !== 1 ? 's' : ''} within {radiusMiles} mile{radiusMiles !== 1 ? 's' : ''}
            </span>
          </div>
          {competitors.length > 0 && (
            <div className="flex items-center gap-2">
              <Navigation className="w-4 h-4 text-blue-600" />
              <span>Closest: {competitors[0].distance_miles?.toFixed(2)} miles</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
